<bic-popup (close)="onClose()" [customClass]="'w-[560px] relative'"
           [title]="newVersion ? 'In Beta bereitstellen' : 'Zentral bereitstellen'">
  <!-- Adminfunktion: das rote Badge sitzt in der Kopfzeile, links neben dem ×. -->
  <app-admin-badge [floating]="true"></app-admin-badge>
  <div bicPopupContent class="flex flex-col gap-4">
    <div class="flex flex-row items-center gap-2 min-w-0">
      <span class="text-sm font-medium text-gray-900 truncate flex-1">{{ itemName }}</span>
      <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">{{ typeLabel }}</bic-badge>
    </div>
    @if (newVersion) {
      <p class="text-sm text-gray-500">
        Der Entwurf wird im gespeicherten Stand sofort bereitgestellt@if (stage) { (in <span class="font-medium">{{ stage }}</span>)}
        und ersetzt die bisherige Fassung für alle Empfänger dort. Nach Live kommt er erst, wenn er im
        Admin Center übernommen wird.
      </p>
    } @else {
      <p class="text-sm text-gray-500">
        Der {{ itemType === 'WORKFLOW' ? 'Prüflauf' : 'myCheck' }} wird im aktuellen Stand sofort für die
        gewählte Zielgruppe bereitgestellt@if (stage) { (in <span class="font-medium">{{ stage }}</span>)}.
        Empfänger können ihn ausführen und duplizieren, aber nicht bearbeiten. Nach Live kommt er erst
        über das Admin Center. Ihr Original bleibt unverändert.
      </p>
    }

    <!-- myCheck als Teil zentraler Prüfläufe: Freigabe-Einheit ist der Prüflauf -->
    @if (parentWorkflows.length === 1) {
      <div class="flex flex-row items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
        <fa-icon [icon]="faCircleInfo" class="text-blue-900 text-sm shrink-0 mt-0.5"></fa-icon>
        <span class="text-xs text-blue-900">
          Aktualisiert Prüflauf <span class="font-medium">{{ parentWorkflows[0].title }}</span>
          – mit allen enthaltenen myChecks im bereitgestellten Stand, dieser myCheck im Stand seines
          Entwurfs. Die Zielgruppe des Prüflaufs bleibt.
        </span>
      </div>
    } @else if (parentWorkflows.length > 1) {
      <div class="flex flex-row items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
        <fa-icon [icon]="faCircleInfo" class="text-blue-900 text-sm shrink-0 mt-0.5"></fa-icon>
        <div class="flex flex-col gap-1 text-xs text-blue-900">
          <span>
            Aktualisiert diese Prüfläufe – mit allen enthaltenen myChecks im bereitgestellten Stand, dieser
            myCheck im Stand seines Entwurfs. Die Zielgruppen der Prüfläufe bleiben:
          </span>
          <ul class="list-disc pl-4">
            @for (workflow of parentWorkflows; track workflow.catalogKey) {
              <li class="font-medium">{{ workflow.title }}</li>
            }
          </ul>
        </div>
      </div>
    }

    <!-- Zielgruppe in dieser Stage -->
    @if (parentWorkflows.length === 0 && !preflightLoading) {
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-gray-900">Zielgruppe</span>
        @if (alreadyProvided) {
          <label class="flex flex-row items-center gap-2 cursor-pointer">
            <input [(ngModel)]="changeTargets" [disabled]="submitting" type="checkbox"
                   class="h-4 w-4 rounded border-gray-300 text-green accent-green focus:ring-green"/>
            <span class="text-sm text-gray-700">Zielgruppe ändern (sonst bleibt die bisherige)</span>
          </label>
        }
        @if (targetsEditable) {
          <app-catalog-targets-picker (targetsChange)="targets = $event" [disabled]="submitting"></app-catalog-targets-picker>
          @if (targets === null) {
            <span class="text-xs text-gray-500">Wählen Sie „Global" oder mindestens eine Organisation bzw. einen Nutzer aus.</span>
          }
        }
      </div>
    }

    <!-- Bisherige Bereitstellungen dieser Quelle -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-gray-900">Bisherige Bereitstellungen</span>
      @if (submissionsLoading) {
        <div class="flex flex-row items-center gap-2">
          <fa-icon [icon]="faSpinnerThird" class="animate-spin text-sm text-gray-500"></fa-icon>
          <span class="text-xs text-gray-500">Bereitstellungen werden geladen...</span>
        </div>
      } @else if (submissionsFailed) {
        <span class="text-xs text-red">Bisherige Bereitstellungen konnten nicht geladen werden.</span>
      } @else if (submissions.length === 0) {
        <span class="text-xs text-gray-500">Noch nicht bereitgestellt.</span>
      } @else {
        <div class="flex flex-col max-h-[140px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
          @for (submission of submissions; track submission.id) {
            <div class="flex flex-col gap-1 px-3 py-2">
              <div class="flex flex-row items-center gap-2">
                <span class="text-xs text-gray-700 flex-1 truncate">
                  {{ submission.submittedAt | date:'dd.MM.yyyy HH:mm' }} · {{ submission.submittedByName }}
                </span>
                @if (submission.status === 'REJECTED') {
                  <bic-badge bgClass="bg-red-100" textClass="text-red">{{ statusLabel(submission.status) }}</bic-badge>
                } @else if (submission.status === 'INSTALLED') {
                  <bic-badge bgClass="bg-green-100" textClass="text-green-900">{{ statusLabel(submission.status) }}</bic-badge>
                } @else {
                  <bic-badge bgClass="bg-blue-100" textClass="text-blue-900">{{ statusLabel(submission.status) }}</bic-badge>
                }
              </div>
              @if (submission.changelog) {
                <span class="text-xs text-gray-500 line-clamp-2">{{ submission.changelog }}</span>
              }
              @if (submission.status === 'REJECTED' && submission.rejectReason) {
                <span class="text-xs text-red">Begründung: {{ submission.rejectReason }}</span>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- Changelog (Pflicht) -->
    <bic-textarea (valueChange)="onChangelogChange($event)"
                  [disabled]="submitting"
                  [label]="'Was hat sich geändert?'"
                  [maxLength]="changelogMaxLength"
                  [required]="true"
                  [rows]="3"
                  [value]="changelog"
                  placeholder="Kurz beschreiben, was neu ist oder sich geändert hat – erscheint im Admin Center als Changelog der Übernahme nach Live."></bic-textarea>

    <!-- Vorprüfung gegen die eigene Stage -->
    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-gray-900">Vorprüfung</span>
      @if (preflightLoading) {
        <div class="flex flex-row items-center gap-2">
          <fa-icon [icon]="faSpinnerThird" class="animate-spin text-sm text-gray-500"></fa-icon>
          <span class="text-xs text-gray-500">Vorprüfung läuft...</span>
        </div>
      } @else if (preflightFailed) {
        <div class="flex flex-row items-center gap-3">
          <span class="text-xs text-red flex-1">Die Vorprüfung ist fehlgeschlagen. Ohne Vorprüfung ist kein Bereitstellen möglich.</span>
          <bic-button (onClick)="runPreflight()" buttonType="white">Erneut prüfen</bic-button>
        </div>
      } @else if (findings.length === 0) {
        <span class="text-xs text-gray-500">Keine Auffälligkeiten.</span>
      } @else {
        @for (finding of blockers; track $index) {
          <div class="flex flex-row items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <fa-icon [icon]="faCircleXmark" class="text-red text-sm shrink-0 mt-0.5"></fa-icon>
            <span class="text-xs font-medium text-red">{{ finding.message }}</span>
          </div>
        }
        @for (finding of warnings; track $index) {
          <div class="flex flex-row items-start gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
            <fa-icon [icon]="faTriangleExclamation" class="text-yellow-700 text-sm shrink-0 mt-0.5"></fa-icon>
            <span class="text-xs font-medium text-yellow-900">{{ finding.message }}</span>
          </div>
        }
      }
    </div>

    @if (blockers.length > 0) {
      <span class="text-xs text-gray-500">Bitte zuerst die rot markierten Punkte beheben.</span>
    }
  </div>

  <div bicPopupButtonGroup class="flex flex-row w-full items-center justify-end gap-4">
    <bic-button (onClick)="onClose()" [disabled]="submitting" buttonType="white">Abbrechen</bic-button>
    <bic-button (onClick)="onSubmit()"
                [disabled]="!canSubmit"
                [loading]="submitting"
                buttonType="primary">
      Bereitstellen
    </bic-button>
  </div>
</bic-popup>
